<script setup lang="ts">
import { ref, nextTick, onMounted, onUnmounted } from 'vue'
import { trackEvent } from '@/utils/analytics'
import { t, isRTL } from '@/i18n'

const isModalOpen = ref(false)
const modalRef = ref<HTMLElement | null>(null)
const isSubmitting = ref(false)
const submitStatus = ref<'success' | 'error' | null>(null)

const formData = ref({
  name: '',
  email: '',
  subject: '',
  message: ''
})

let previouslyFocused: HTMLElement | null = null

function openModal() {
  previouslyFocused = document.activeElement as HTMLElement | null
  isModalOpen.value = true
  document.body.style.overflow = 'hidden'
  trackEvent('contact_feedback_open')
  nextTick(() => {
    modalRef.value?.focus()
  })
}

function closeModal() {
  isModalOpen.value = false
  document.body.style.overflow = ''
  if (submitStatus.value === 'success') {
    resetForm()
  }
  previouslyFocused?.focus()
}

function handleFocusTrap(event: KeyboardEvent) {
  if (event.key !== 'Tab' || !modalRef.value) return
  const focusable = modalRef.value.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )
  if (focusable.length === 0) return
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (event.shiftKey && (document.activeElement === first || document.activeElement === modalRef.value)) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

function resetForm() {
  formData.value = {
    name: '',
    email: '',
    subject: '',
    message: ''
  }
  submitStatus.value = null
}

async function handleSubmit() {
  isSubmitting.value = true
  submitStatus.value = null

  try {
    const response = await fetch('https://formspree.io/f/mblnwzwz', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        name: formData.value.name,
        email: formData.value.email,
        subject: `[cutmp3.online] ${formData.value.subject}`.trim(),
        message: formData.value.message
      })
    })

    if (response.ok) {
      submitStatus.value = 'success'
      trackEvent('contact_feedback_submit')
    } else {
      submitStatus.value = 'error'
    }
  } catch (error) {
    submitStatus.value = 'error'
    if (import.meta.env.DEV) console.error('[ContactSection] Submit error:', error)
  } finally {
    isSubmitting.value = false
  }
}

function handleBackdropClick(event: MouseEvent) {
  if (event.target === event.currentTarget) {
    closeModal()
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (!isModalOpen.value) return
  if (event.key === 'Escape') {
    closeModal()
  }
  handleFocusTrap(event)
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = ''
})
</script>

<template>
  <section id="contact" class="contact">
    <div class="container text-center">
      <div class="contact-card card">
        <h2 class="contact-title text-primary">
          {{ t.contactTitle }}
        </h2>
        <p class="contact-text text-secondary">
          {{ t.contactText }}
        </p>
        <div class="contact-actions">
          <button class="btn btn-primary" @click="openModal">
            {{ t.contactButton }}
          </button>
        </div>
      </div>
    </div>

    <!-- Feedback Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="isModalOpen" class="modal-overlay" @click="handleBackdropClick">
          <div ref="modalRef" class="modal-content contact-modal" :dir="isRTL ? 'rtl' : 'ltr'" role="dialog" aria-modal="true" aria-labelledby="modal-title" tabindex="-1">
            <button class="modal-close" :aria-label="t.contactClose" @click="closeModal">
              ✕
            </button>

            <h3 id="modal-title" class="modal-title text-center">
              {{ t.contactTitle }}
            </h3>

            <!-- Success Message -->
            <div v-if="submitStatus === 'success'" class="status-msg status-success">
              <div class="status-icon">✓</div>
              <h4>{{ t.contactSuccessTitle }}</h4>
              <p>{{ t.contactSuccessText }}</p>
              <button class="btn btn-primary w-full" @click="closeModal">
                {{ t.contactClose }}
              </button>
            </div>

            <!-- Error Message -->
            <div v-else-if="submitStatus === 'error'" class="status-msg status-error">
              <div class="status-icon">✕</div>
              <h4>{{ t.contactErrorTitle }}</h4>
              <p>
                {{ t.contactErrorText }}
                <a :href="`mailto:${t.contactEmailLink}`" class="contact-link">{{ t.contactEmailLink }}</a>
              </p>
              <button class="btn btn-primary w-full" @click="submitStatus = null">
                {{ t.contactRetry }}
              </button>
            </div>

            <!-- Form -->
            <form v-else class="contact-form" @submit.prevent="handleSubmit">
              <div class="form-group">
                <label for="contact-name" class="form-label">
                  {{ t.contactName }} <span class="required-mark" aria-hidden="true">*</span>
                  <span class="sr-only">{{ t.required }}</span>
                </label>
                <input id="contact-name" v-model="formData.name" type="text" class="form-input"
                  :placeholder="t.contactNamePlaceholder" autocomplete="name" required>
              </div>

              <div class="form-group">
                <label for="contact-email" class="form-label">
                  {{ t.contactEmail }} <span class="required-mark" aria-hidden="true">*</span>
                  <span class="sr-only">{{ t.required }}</span>
                </label>
                <input id="contact-email" v-model="formData.email" type="email" class="form-input"
                  :placeholder="t.contactEmailPlaceholder" dir="ltr" autocomplete="email" required>
              </div>

              <div class="form-group">
                <label for="contact-subject" class="form-label">
                  {{ t.contactSubject }}
                </label>
                <input id="contact-subject" v-model="formData.subject" type="text" class="form-input"
                  :placeholder="t.contactSubjectPlaceholder">
              </div>

              <div class="form-group">
                <label for="contact-message" class="form-label">
                  {{ t.contactMessage }} <span class="required-mark" aria-hidden="true">*</span>
                  <span class="sr-only">{{ t.required }}</span>
                </label>
                <textarea id="contact-message" v-model="formData.message" class="form-input contact-textarea"
                  :placeholder="t.contactMessagePlaceholder" rows="4" required />
              </div>

              <div class="contact-modal-actions">
                <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
                  <span v-if="isSubmitting" class="btn-spinner" />
                  {{ isSubmitting ? t.contactSubmitting : t.contactSubmit }}
                </button>
                <button type="button" class="btn btn-secondary" @click="closeModal">
                  {{ t.contactCancel }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<style scoped>
.contact {
  padding: 4rem 0;
}

.contact-card {
  max-width: 600px;
  margin: 0 auto;
  padding: 2.5rem;
}

.contact-title {
  margin-bottom: 1rem;
}

.contact-text {
  margin-bottom: 2rem;
  line-height: 1.8;
}

.contact-modal {
  padding: 2rem;
  position: relative;
}

.modal-close {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  font-size: 1.25rem;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: 50%;
  transition: background-color var(--transition-fast), color var(--transition-fast);
}

.required-mark {
  color: var(--error-color);
  font-weight: 600;
}

.modal-close:hover {
  background-color: var(--border-color);
  color: var(--text-primary);
}

.modal-close:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

/* LTR support: close button on right side */
[dir="ltr"] .modal-close {
  left: auto;
  right: 1rem;
}

.modal-title {
  margin-bottom: 2rem;
  color: var(--text-primary);
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* LTR support: form labels align left */
[dir="ltr"] .contact-form .form-label {
  text-align: left;
}

[dir="ltr"] .contact-form .form-input {
  text-align: left;
}

.contact-textarea {
  resize: vertical;
  min-height: 100px;
}

.contact-modal-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
}

.contact-modal-actions .btn {
  flex: 1;
}

/* Status Messages */
.status-msg {
  text-align: center;
  padding: 1rem 0;
}

.status-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  font-size: 2rem;
  margin-bottom: 1.5rem;
}

.status-success .status-icon {
  background-color: var(--status-success-bg);
  color: var(--status-success-fg);
}

.status-error .status-icon {
  background-color: var(--status-error-bg);
  color: var(--status-error-fg);
}

.status-msg h4 {
  margin-bottom: 0.5rem;
  color: var(--text-primary);
}

.status-msg p {
  color: var(--text-secondary);
  margin-bottom: 2rem;
}

.contact-link {
  color: var(--primary-color);
  text-decoration: underline;
  word-break: break-all;
}

/* Spinner */
.btn-spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-left: 0.5rem;
}

[dir="ltr"] .btn-spinner {
  margin-left: 0;
  margin-right: 0.5rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity var(--transition-normal);
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform var(--transition-normal), opacity var(--transition-normal);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.95) translateY(-20px);
  opacity: 0;
}

/* Responsive */
@media (max-width: 640px) {
  .contact {
    padding: 2.5rem 0;
  }
  
  .contact-card {
    padding: 1.5rem;
  }
  
  .contact-modal {
    padding: 1.5rem;
  }
  
  .contact-modal-actions {
    flex-direction: column;
  }
}
</style>
